import type { WorkingDay } from './hours.ts';

export type HoursClient = {
  /** `'me'` is the signed-in person: the browser's token is opaque, so it
   *  cannot know its own person id and the service resolves it. */
  read(person: string): Promise<WorkingDay[]>;
  set(person: string, days: readonly WorkingDay[]): Promise<WorkingDay[]>;
};

export class HoursError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** The client for `apps/hr`, through the agora's own `/_hr` proxy. */
export function createHoursClient(options: {
  url: string;
  getAccessToken: () => string;
  fetch?: typeof fetch;
}): HoursClient {
  const call = async (person: string, init: RequestInit): Promise<WorkingDay[]> => {
    const response = await (options.fetch ?? fetch)(
      `${options.url}/v1/working-hours/${encodeURIComponent(person)}`,
      {
        ...init,
        headers: {
          authorization: `Bearer ${options.getAccessToken()}`,
          ...(init.body ? { 'content-type': 'application/json' } : {}),
        },
      },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      throw new HoursError(response.status, body.message ?? `Working hours ${response.status}`);
    }
    return ((await response.json()) as { days: WorkingDay[] }).days;
  };
  return {
    read: (person) => call(person, { method: 'GET' }),
    set: (person, days) => call(person, { method: 'PUT', body: JSON.stringify({ days }) }),
  };
}
